import { useTranslation } from 'react-i18next';
import ReactModal from 'react-modal';
import React, { ForwardedRef, forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { focusCloseButton, IFormModalRef, reactModalProps } from '../../modal';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import DropDownField from '../../../form/dropdownField';
import {
    EligibilityModalResultKeys,
    IEligibilityModalFormResultModel,
} from './formEligibilityModalModel';
import getIssuers from '../../../../helpers/issuers';
import i18n from 'i18next';
import { IIssuerModel } from '../../../../helpers/models/issuerModel';
import InputField from '../../../form/inputField';
import Notification, { INotificationErrorMessage } from '../../../notification/notification';
import fetchFormData from '../../../../helpers/fetchFormData';
import { handleRequiredError, scrollToFormErrors } from '../../../registration/utils/formErrors';
import { createEligibility } from '../../../../helpers/eligibility';
import { IEligibilityModel } from '../../../../helpers/models/eligibilityModel';
import {
    FORM_FIRSTNAME_MAX_LENGTH,
    FORM_LASTNAME_MAX_LENGTH,
} from '../../../registration/utils/formValidation';

export interface IFormEligibilityModal {
    provinces: IProvinceModel[];
    roles: IRoleModel[];
}

export interface IFormEligibilityModalRef {
    setSelectedProvince(value: string): void;
    setSelectedRole(value: string): void;
}

const FormEligibilityModal = (props: IFormEligibilityModal, ref: ForwardedRef<IFormModalRef & IFormEligibilityModalRef>) => {
    const { provinces, roles } = props;
    const { t } = useTranslation();
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [description, setDescription] = useState<string>('');
    const [resultMessage, setResultMessage] = useState<string>('');

    const [selectedProvince, setSelectedProvince] = useState<string>('');
    const [selectedRole, setSelectedRole] = useState<string>('');
    const [issuers, setIssuers] = useState<IIssuerModel[]>([]);

    const [errors, setErrors] = useState<INotificationErrorMessage[]>([]);
    useEffect(() => {
        if (!modalIsOpen) {
            document.body.classList.remove('modal-open');

            setIssuers([]);
            setSelectedProvince('');
            setSelectedRole('');
            setDescription('');
            setResultMessage('');
        } else {
            focusCloseButton();
        }
    }, [modalIsOpen, selectedProvince, selectedRole]);

    useImperativeHandle(ref, () => {
        return {
            setModalOpen(value: boolean) {
                setModalIsOpen(value);
            },
            setSelectedProvince(value: string) {
                setSelectedProvince(value);
            },
            setSelectedRole(value: string) {
                setSelectedRole(value)
            }
        };
    });

    ReactModal.setAppElement('#root');

    const roleComponents = roles.map((item: IRoleModel) => {
        return (
            <option key={item.id} value={item.id}>
                {item.name}
            </option>
        );
    });

    const provinceComponents = provinces.map((item: IProvinceModel) => {
        return (
            <option key={item.id} value={item.id}>
                {item.name}
            </option>
        );
    });

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        const data = fetchFormData(e);
        const formResult = Object.assign(
            {},
            ...data.map((x) => ({ [x.name]: x.value }))
        ) as IEligibilityModalFormResultModel;

        const errorList = handleRequiredError(formResult, data, t);
        document.getElementById('eligibility-notification-container')!.style.display =
            errorList.length ? 'unset' : 'none';

        if (!errorList.length) {
            const eligibility = {
                firstname: formResult.form_modal_eligibility_first_name,
                lastname: formResult.form_modal_eligibility_last_name,
                email: formResult.form_modal_eligibility_email,
                role: +formResult.form_modal_eligibility_role,
                province: +formResult.form_modal_eligibility_province,
            } as IEligibilityModel;
            const result = await createEligibility(i18n.language, eligibility);
            if (result) {
                setResultMessage('form_modal_eligibility_success');
                document.getElementById('eligibility-result')!.className = 'eligibility-success';

                (
                    document.getElementById(
                        EligibilityModalResultKeys.FIRST_NAME
                    )! as HTMLInputElement
                ).value = '';
                (
                    document.getElementById(
                        EligibilityModalResultKeys.LAST_NAME
                    )! as HTMLInputElement
                ).value = '';
                (
                    document.getElementById(EligibilityModalResultKeys.EMAIL)! as HTMLInputElement
                ).value = '';
            } else {
                setResultMessage('form_modal_eligibility_error');
                document.getElementById('eligibility-result')!.className = 'eligibility-error';
            }
        } else {
            scrollToFormErrors();
        }
        setErrors(errorList);
    };

    const handleOnProvinceChange = async (e: any) => {
        setSelectedProvince(e.target.value);
    };

    const handleOnRoleChange = async (e: any) => {
        setSelectedRole(e.target.value);
    };

    useEffect(() => {
        async function getData() {
            if (selectedProvince && selectedRole) {
                const issuerList = await getIssuers(
                    i18n.language,
                    +selectedRole,
                    +selectedProvince
                );
                document.getElementById('eligibility-submit-container')!.style.display =
                    issuerList?.length ? 'none' : 'block';

                if (issuerList?.length) {
                    setDescription('form_modal_eligibility_eligible');
                    setIssuers(issuerList ?? []);
                } else {
                    setDescription('form_modal_eligibility_not_eligible');
                    setIssuers([]);
                }
            } else {
                setDescription('');
            }
        }
        getData();
    }, [selectedProvince, selectedRole]);

    const issuerComponents = issuers.map((element: IIssuerModel) => {
        return <li key={element.id}>{element.name}</li>;
    });

    // Massage therapists in Quebec have an additional training-hours requirement.
    const selectedRoleCode = roles.find((role) => role.id === selectedRole)?.code;
    const selectedProvinceCode = provinces.find((province) => province.id === selectedProvince)?.code;
    const showMassageTrainingNote =
        description === 'form_modal_eligibility_eligible' &&
        selectedRoleCode === 'LMT' &&
        selectedProvinceCode === 'QC';

    return (
        <ReactModal
            aria-label={t('modal_eligibility_container_aria_label', { ns: 'accessibility' })}
            isOpen={modalIsOpen}
            onRequestClose={() => setModalIsOpen(false)}
            contentLabel={t('form_modal_eligibility_title')}
            {...reactModalProps}
        >
            <button
                className="modal__close-icon"
                onClick={() => setModalIsOpen(false)}
                aria-label={t('modal_close_aria_label', { ns: 'accessibility' })}
                id="modal_close_button"
            >
                <span className="visuallyhidden">
                    {t('modal_close_aria_label', { ns: 'accessibility' })}
                </span>
                <span aria-hidden="true">×</span>
            </button>
            <div>
                <h2 className="header-1 modal-header">{t('form_modal_eligibility_title')}</h2>
                <form id="eligibility-form" className="body-large" onSubmit={handleSubmit}>
                    <div className="eligibility-inputs-container modal-bottom-element body-medium">
                        <div className="eligibility-inputs-two-column">
                            <DropDownField
                                fieldContainerClassName="field-container--large registration-column-item"
                                fieldName={EligibilityModalResultKeys.PROVINCE}
                                fieldTitle="form_modal_eligibility_province"
                                fieldOptions={provinceComponents}
                                fieldDefaultValue="form_contact_practice_state_default"
                                defaultValue={selectedProvince ?? ''}
                                handleOnChange={handleOnProvinceChange}
                                fieldRequired
                                fieldWeight={0}
                            />
                            <DropDownField
                                fieldContainerClassName="field-container--large registration-column-item"
                                fieldName={EligibilityModalResultKeys.SPECIALIST_GROUP}
                                fieldTitle="form_modal_eligibility_role"
                                fieldOptions={roleComponents}
                                fieldDefaultValue="form_contact_practice_specialist_group_default"
                                defaultValue={selectedRole ?? ''}
                                handleOnChange={handleOnRoleChange}
                                fieldRequired
                                fieldWeight={1}
                            />
                        </div>
                        {showMassageTrainingNote ? (
                            <div>
                                {t('form_modal_eligibility_eligible_massage')}{' '}
                                <strong className="eligibility-note-emphasis">
                                    {t('form_modal_eligibility_eligible_massage_bold')}
                                </strong>
                            </div>
                        ) : (
                            <div>{t(description)}</div>
                        )}
                        {issuers.length ? <ul>{issuerComponents}</ul> : null}
                    </div>
                    <div id="eligibility-submit-container" className="eligibility-submit-container">
                        <div
                            id="eligibility-notification-container"
                            className="registration-error-container eligibility-notification-container"
                        >
                            <Notification error messages={errors} />
                        </div>
                        <div className="eligibility-column-container">
                            <InputField
                                fieldContainerClassName="eligibility-column-item"
                                fieldName={EligibilityModalResultKeys.FIRST_NAME}
                                fieldTitle="form_modal_eligibility_first_name"
                                fieldRequired
                                fieldWeight={2}
                                maxLength={FORM_FIRSTNAME_MAX_LENGTH}
                            />
                            <InputField
                                fieldContainerClassName="eligibility-column-item"
                                fieldName={EligibilityModalResultKeys.LAST_NAME}
                                fieldTitle="form_modal_eligibility_last_name"
                                fieldRequired
                                fieldWeight={3}
                                maxLength={FORM_LASTNAME_MAX_LENGTH}
                            />
                        </div>
                        <div className="eligibility-column-container">
                            <InputField
                                fieldContainerClassName="eligibility-column-item"
                                fieldName={EligibilityModalResultKeys.EMAIL}
                                fieldTitle="form_modal_eligibility_email"
                                fieldRequired
                                type="email"
                                fieldWeight={4}
                            />
                        </div>
                        <div id="modal-button-notify" className="modal-center-button-container">
                            <button
                                id="eligibility-submit-button"
                                type="submit"
                                className="button button--primary modal-center-button"
                            >
                                {t('form_modal_eligibility_notify_button')}
                            </button>
                        </div>
                        <div id="eligibility-result">{t(resultMessage)}</div>
                    </div>
                </form>
            </div>
        </ReactModal>
    );
};

export default forwardRef(FormEligibilityModal);
